// Auto-generated by splitter.py — do not regenerate; edit directly.

import React from 'react';
import { MessageSquare, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { getVisibleNav } from '../../constants/nav';
import { BRAND } from '../../lib/brand';
import { isTrimmedForMoments } from '../../lib/accessGating';

// Brand logo tile (falls back to a monogram if the image fails to load).
const BrandMark = ({ size }) => (
  <img src={BRAND.logo} alt={BRAND.name} className={`${size} rounded-md object-contain bg-white`} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
);

export const Sidebar = ({ active, onSelect, collapsed = false, onToggle, statusLabel = 'Live data', statusTone = 'green', onFeedback, liveBadges = {}, userEmail = null }) => {
  const statusDot = statusTone === 'green' ? 'bg-[#2F8B4F]' : statusTone === 'red' ? 'bg-[#C53030]' : 'bg-[#C48319]';
  const nav = getVisibleNav(isTrimmedForMoments(userEmail));
  return (
    <aside className={`${collapsed ? 'w-[60px]' : 'w-[220px]'} flex-shrink-0 bg-white border-r border-[#E6E4E1] flex flex-col h-screen sticky top-0 transition-[width] duration-150`}>
      {/* Brand.
          h-14 — THE SAME FIXED HEIGHT AS TopBar's header, not padding around whatever is inside.
          Both blocks draw a bottom border across the top of the screen and the eye reads them as
          one line, so any difference shows up as a step at the sidebar's edge. Measured before this
          change: top bar 56px, brand 62px expanded and 69px collapsed — the border sat 6px low, and
          moved another 7px on collapse, because the collapsed logo (36px) is taller than the
          expanded logo-and-two-lines (~30px) and the padding just followed it.
          `shrink-0` so the nav below can never squeeze it back out of alignment. */}
      <div className={`${collapsed ? 'px-2 justify-center' : 'px-4'} h-14 shrink-0 border-b border-[#E6E4E1] flex items-center`}>
        {collapsed ? (
          <BrandMark size="w-8 h-8" title={`${BRAND.name} Back Office`} />
        ) : (
          <div className="flex items-center gap-2.5 w-full">
            <BrandMark size="w-7 h-7" />
            <div className="leading-tight flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#1F2937] truncate">Back Office</div>
              <div className="text-[10.5px] text-[#6B7280]">Internal Platform Admin</div>
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className={`flex-1 overflow-y-auto py-2 ${collapsed ? 'px-1.5' : 'px-2'}`}>
        {nav.map((group, gi) => (
          <div key={gi} className={gi === 0 ? '' : 'mt-4'}>
            {group.section && !collapsed && (
              <div className="px-2 pt-2 pb-1.5 text-[10px] tracking-[0.1em] font-semibold text-[#9CA3AF]">{group.section}</div>
            )}
            {group.section && collapsed && gi > 0 && (
              <div className="mx-2 my-2 border-t border-[#EEEDEA]" />
            )}
            {group.items.map(it => {
              const Icon = it.icon;
              const isActive = active === it.id;
              // A real, per-item override (e.g. Call Center's "a call is
              // genuinely ringing right now") beats the static config —
              // never a permanently-on decorative badge.
              const badge = liveBadges[it.id] !== undefined ? liveBadges[it.id] : it.badge;
              return (
                <button
                  key={it.id}
                  onClick={() => onSelect(it.id)}
                  title={collapsed ? it.label : undefined}
                  className={`w-full flex items-center ${collapsed ? 'justify-center px-2' : 'gap-2.5 px-2'} py-1.5 rounded-md text-[12.5px] font-medium transition-colors text-left relative ${
                    isActive ? 'bg-[#F2F1ED] text-[#1F2937]' : 'text-[#4B5563] hover:bg-[#F7F6F2] hover:text-[#1F2937]'
                  }`}
                >
                  <Icon className="w-[15px] h-[15px] flex-shrink-0" strokeWidth={1.75} />
                  {!collapsed && <span className="flex-1 truncate">{it.label}</span>}
                  {badge === 'dot' && (
                    <span className={`${collapsed ? 'absolute top-1 right-1' : ''} w-1.5 h-1.5 rounded-full bg-[#C53030]`} />
                  )}
                  {badge === 'live' && !collapsed && (
                    <span className="flex items-center gap-1 text-[9.5px] font-semibold text-[#1F6A3A]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#2F8B4F] animate-pulse" />LIVE
                    </span>
                  )}
                  {badge === 'live' && collapsed && (
                    <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#2F8B4F] animate-pulse" />
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Sidebar toggle + Feedback + Footer */}
      <div className={` flex border-t border-[#E6E4E1] ${collapsed ? 'px-2 py-2' : 'px-3 py-2.5'} text-[10.5px] text-[#6B7280]`}>
        {onFeedback &&!collapsed && (
          <button onClick={onFeedback} className={`w-full flex flex-1 items-center gap-2 px-2 py-1.5 rounded-md text-[#6B7280] hover:bg-[#F7F6F2] hover:text-[#1F2937] mb-1 ${collapsed ? 'justify-center' : ''}`}>
            <MessageSquare className="w-[15px] h-[15px] flex-shrink-0" strokeWidth={1.75} />
            {!collapsed && <span className="text-[11px]">Send feedback</span>}
          </button>
        )}
        <button onClick={onToggle} aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} className="w-full_ justify-center flex_ items-center px-2 py-1.5 rounded-md text-[#6B7280] hover:bg-[#F7F6F2] hover:text-[#1F2937]">
          {collapsed ? <PanelLeftOpen className="w-[15px] h-[15px]" strokeWidth={1.75} /> : <PanelLeftClose className="w-[15px] h-[15px]" strokeWidth={1.75} />}
        </button>
      </div>
    </aside>
  );
};

/* ============================================================================
   TOP BAR
   ============================================================================ */
